import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { CustomerAddress, CustomerProfile } from "@emynor/storefront-sdk";
import { storefrontSdk } from "./sdk";
import type { Address, User } from "./types";
import { storedPromotionAttributionToken } from "./promotion-attribution";

interface AuthValue {
  user: User | null;
  isAuthenticated: boolean;
  hydrated: boolean;
  loginOpen: boolean;
  openLogin: (onSuccess?: () => void) => void;
  closeLogin: () => void;
  sendCode: (phone: string) => Promise<string | null>;
  resend: (phone: string) => Promise<string | null>;
  verify: (phone: string, code: string) => Promise<"authenticated" | "registration_required">;
  completeRegistration: (name: string) => Promise<void>;
  logout: () => void;
  upsertAddress: (address: Address) => Promise<void>;
  deleteAddress: (addressId: string) => Promise<void>;
  updateProfile: (patch: Partial<User>) => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const challenge = useRef<{ id: string; phone: string } | null>(null);
  const registrationToken = useRef<string | null>(null);
  const pending = useRef<(() => void) | null>(null);

  const loadUser = useCallback(async (profile?: CustomerProfile) => {
    const currentProfile = profile ?? (await storefrontSdk.profile());
    const addresses = await storefrontSdk.addresses();
    setUser(userFromContract(currentProfile, addresses));
  }, []);

  useEffect(() => {
    let active = true;
    storefrontSdk
      .restoreSession()
      .then(async (restored) => {
        if (restored) await loadUser();
      })
      .catch(() => storefrontSdk.clearSession())
      .finally(() => {
        if (active) setHydrated(true);
      });
    return () => {
      active = false;
    };
  }, [loadUser]);

  const openLogin = useCallback((onSuccess?: () => void) => {
    pending.current = onSuccess ?? null;
    setLoginOpen(true);
  }, []);

  const closeLogin = useCallback(() => {
    pending.current = null;
    setLoginOpen(false);
  }, []);

  const sendCode = useCallback(async (phone: string) => {
    const result = await storefrontSdk.startOtp({ phone });
    challenge.current = { id: result.challengeId, phone };
    registrationToken.current = null;
    return result.simulatorCode ?? null;
  }, []);

  const resend = useCallback(
    (phone: string) => sendCode(phone),
    [sendCode],
  );

  const verify = useCallback(
    async (phone: string, code: string): Promise<"authenticated" | "registration_required"> => {
      if (!challenge.current || challenge.current.phone !== phone)
        throw new Error("OTP challenge is missing");
      const promotionAttributionToken = storedPromotionAttributionToken();
      const result = await storefrontSdk.verifyOtp({
        challengeId: challenge.current.id,
        phone,
        code,
        ...(promotionAttributionToken ? { promotionAttributionToken } : {}),
      });
      if (result.status === "registration_required") {
        registrationToken.current = result.registrationToken;
        return "registration_required";
      }
      await loadUser(result.customer);
      challenge.current = null;
      registrationToken.current = null;
      setLoginOpen(false);
      const callback = pending.current;
      pending.current = null;
      callback?.();
      return "authenticated";
    },
    [loadUser],
  );

  const completeRegistration = useCallback(
    async (name: string) => {
      if (!challenge.current || !registrationToken.current)
        throw new Error("Phone verification has expired");
      const promotionAttributionToken = storedPromotionAttributionToken();
      const result = await storefrontSdk.completeOtpRegistration({
        challengeId: challenge.current.id,
        registrationToken: registrationToken.current,
        name: name.trim(),
        ...(promotionAttributionToken ? { promotionAttributionToken } : {}),
      });
      await loadUser(result.customer);
      challenge.current = null;
      registrationToken.current = null;
      setLoginOpen(false);
      const callback = pending.current;
      pending.current = null;
      callback?.();
    },
    [loadUser],
  );

  const logout = useCallback(() => {
    void storefrontSdk.logout().catch(() => undefined);
    setUser(null);
  }, []);

  const upsertAddress = useCallback(
    async (address: Address) => {
      const input = {
        label: address.label,
        street: address.street,
        area: address.area,
        building: address.building,
        floor: address.floor,
        apartment: address.apartment,
        landmark: address.landmark,
        instructions: address.note ?? "",
        location: { lat: address.lat, lng: address.lng },
      };
      const existing = user?.addresses.find(
        (candidate) => candidate.id === address.id,
      );
      if (existing && addressesMatch(existing, address)) return;
      const saved = existing
        ? await storefrontSdk.updateAddress(address.id, input)
        : await storefrontSdk.createAddress(input);
      setUser((current) => {
        if (!current) return current;
        const next = addressFromContract(saved, current);
        return {
          ...current,
          addresses: current.addresses.some(
            (candidate) => candidate.id === saved.id,
          )
            ? current.addresses.map((candidate) =>
                candidate.id === saved.id ? next : candidate,
              )
            : [...current.addresses, next],
        };
      });
    },
    [user],
  );

  const deleteAddress = useCallback(async (addressId: string) => {
    await storefrontSdk.deleteAddress(addressId);
    setUser((current) =>
      current
        ? {
            ...current,
            addresses: current.addresses.filter(
              (address) => address.id !== addressId,
            ),
          }
        : current,
    );
  }, []);

  const updateProfile = useCallback(
    async (patch: Partial<User>) => {
      if (!user) return;
      const updated = await storefrontSdk.updateProfile({
        name: patch.name?.trim() || user.name,
      });
      setUser((current) => {
        if (!current) return current;
        const phone = updated.phone_e164 ?? "";
        return {
          ...current,
          name: updated.name,
          phone,
          verified:
            updated.phone_verified_at === undefined
              ? current.verified
              : Boolean(updated.phone_verified_at),
          addresses: current.addresses.map((address) => ({
            ...address,
            fullName: updated.name,
            phone,
          })),
        };
      });
    },
    [user],
  );

  const value = useMemo<AuthValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      hydrated,
      loginOpen,
      openLogin,
      closeLogin,
      sendCode,
      resend,
      verify,
      completeRegistration,
      logout,
      upsertAddress,
      deleteAddress,
      updateProfile,
    }),
    [
      user,
      hydrated,
      loginOpen,
      openLogin,
      closeLogin,
      sendCode,
      resend,
      verify,
      completeRegistration,
      logout,
      upsertAddress,
      deleteAddress,
      updateProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

function userFromContract(
  profile: CustomerProfile,
  addresses: CustomerAddress[],
): User {
  const phone = profile.phone_e164 ?? "";
  return {
    id: profile.id,
    name: profile.name,
    phone,
    verified: Boolean(profile.phone_verified_at),
    addresses: addresses.map((address) =>
      addressFromContract(address, { name: profile.name, phone }),
    ),
  };
}

function addressFromContract(
  address: CustomerAddress,
  owner: Pick<User, "name" | "phone">,
): Address {
  return {
    id: address.id,
    label: localized(address.label_i18n),
    fullName: owner.name,
    phone: owner.phone,
    street: address.street,
    area: address.area,
    building: address.building,
    floor: address.floor,
    apartment: address.apartment,
    landmark: address.landmark,
    ...(address.instructions ? { note: address.instructions } : {}),
    lat: address.lat,
    lng: address.lng,
  };
}

function addressesMatch(current: Address, next: Address): boolean {
  return (
    current.label.en === next.label.en &&
    current.label.ar === next.label.ar &&
    current.street === next.street &&
    current.area === next.area &&
    current.building === next.building &&
    current.floor === next.floor &&
    current.apartment === next.apartment &&
    current.landmark === next.landmark &&
    (current.note ?? "") === (next.note ?? "") &&
    current.lat === next.lat &&
    current.lng === next.lng
  );
}

function localized(value: Record<string, string>): { en: string; ar: string } {
  return {
    en: value["en"] ?? value["ar"] ?? "Home",
    ar: value["ar"] ?? value["en"] ?? "المنزل",
  };
}
